/**
 * MEHRERE mitlaufende CTAs übereinander (Jans Umbau 27.09.2026): Der
 * Pro-Editor legt CTAs jetzt wie Assets an, jeder mit eigener Spanne
 * „von Slide X bis Slide Y". Stehen zwei zugleich, liegen sie ÜBEREINANDER
 * am unteren Rand — der zuerst angelegte zuunterst.
 *
 * Ein unsichtbarer Streifen darf keinen Platz belegen, sonst schwebt der
 * sichtbare über einer Lücke. Deshalb liegt ein ausgeblendeter bzw. gerade
 * hinausgleitender Streifen ABSOLUT am unteren Rand (ohne Platz), ein
 * kommender oder stehender normal im Stapel. Bewegt wird nur per transform,
 * nie per Höhe (Jans iPhone-Fund 27.09.2026: eine Höhen-Animation ruckelt).
 *
 * Warum absolut statt „Platz erst nach dem Gleiten wegnehmen" (#1145): Bei
 * einer Serie (Rating auf Slide 2, 3, 4) stand der neue CTA sonst während des
 * Wechsels AUF dem hinausgleitenden, zu hoch — darunter sah man kurz den
 * Hintergrund, dann sprang er nach unten (Jans iPhone-Fund 27.09.2026).
 *
 * AM SCROLLEN FESTGEMACHT (Jans iPhone-Funde 27.09.2026, s. ctaAuftritt):
 * Der Stapel liest in jedem Bild die Zeit des Scrollys und setzt Lage und
 * Deckkraft direkt am DOM — ohne React-Neuaufbau, damit es auf dem iPhone
 * flüssig bleibt. React erfährt nur, WELCHE CTAs gerade da sind (für Klicks
 * und das Zurücksetzen des ×).
 */
import { useLayoutEffect, useRef, useState } from "react";
import type { FunctionComponent } from "react";
import type { CtaAbgabe, ResolvedRunningCta } from "@scrolly/post-types";
import { ctaAuftritt } from "@scrolly/post-types";
import RunningCtaStrip from "./RunningCtaStrip";

interface Props {
    strips: ResolvedRunningCta[];
    /** Aktuelle Zeit + Anfangszeit jeder Slide (Leser: Scroll, Editor: Playhead). */
    zeitQuelle: () => { zeit: number; zeiten: readonly number[] } | null;
    editorMode?: boolean;
    /** Nur bei echten Lesern gesetzt (s. VisualStoryPlayer.onCtaAntwort). */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
}

/** Ausgangslage vor dem ersten Takt: draußen, ohne Platz. */
const DRAUSSEN = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    opacity: 0,
    visibility: "hidden",
    transform: "translate3d(0, 110%, 0)",
    willChange: "transform, opacity",
} as const;

const RunningCtaStapel: FunctionComponent<Props> = ({ strips, zeitQuelle, editorMode, onCtaAntwort }) => {
    const huellen = useRef<(HTMLDivElement | null)[]>([]);
    /**
     * REACTION, in die gerade getippt wird: bleibt ganz da, egal wohin die
     * Seite rutscht (die Handy-Tastatur verschiebt sie oft — und mit ihr die
     * Slide). Nach dem Tippen folgt der CTA wieder dem Scrollen.
     */
    const festgehalten = useRef(new Set<number>());
    // welche gerade (teilweise) da sind — nur bei Wechsel neu gerendert
    const [da, setDa] = useState("");
    const quelle = useRef(zeitQuelle);
    quelle.current = zeitQuelle;

    // LayoutEffect: erster Takt vor dem ersten Bild, sonst blitzte die
    // Ausgangslage kurz auf
    useLayoutEffect(() => {
        let raf = 0;
        let zuletzt = "";
        const setze = () => {
            const q = quelle.current();
            if (!q || q.zeiten.length === 0) return;
            let kennung = "";
            strips.forEach((strip, i) => {
                const { anteil, geht } = festgehalten.current.has(i)
                    ? { anteil: 1, geht: false }
                    : ctaAuftritt(strip, strips, q.zeit, q.zeiten);
                if (anteil > 0) kennung += `${i},`;
                const el = huellen.current[i];
                if (!el) return;
                const imStapel = anteil > 0 && !geht;
                el.style.position = imStapel ? "relative" : "absolute";
                // der kommende liegt immer ÜBER dem gehenden
                el.style.zIndex = imStapel ? "1" : "0";
                el.style.transform = `translate3d(0, ${((1 - anteil) * 110).toFixed(2)}%, 0)`;
                el.style.opacity = anteil.toFixed(3);
                el.style.visibility = anteil > 0 ? "visible" : "hidden";
            });
            if (kennung !== zuletzt) {
                zuletzt = kennung;
                setDa(kennung);
            }
        };
        const bild = () => {
            setze();
            raf = requestAnimationFrame(bild);
        };
        raf = requestAnimationFrame(bild);
        // Rückfall, falls das Bild-Signal ruht (verdeckter Tab)
        const takt = window.setInterval(setze, 250);
        setze();
        return () => {
            cancelAnimationFrame(raf);
            window.clearInterval(takt);
        };
    }, [strips]);

    const daListe = da.split(",");
    return (
        // data-cta-index: der Export in „My Scrollys" (Jan 16.10.2026: „CTAs
        // werden nicht mitgerendert") findet die Streifen darüber.
        <div
            data-cta-stapel=""
            style={{ position: "relative", display: "flex", flexDirection: "column-reverse", pointerEvents: "none" }}
        >
            {strips.map((strip, i) => (
                <div
                    key={strip.id || i}
                    data-cta-index={i}
                    ref={(el) => {
                        huellen.current[i] = el;
                    }}
                    // Nur die Ausgangslage — ab dann setzt der Takt oben Lage
                    // und Deckkraft direkt am DOM. React fasst diese Werte
                    // danach nicht mehr an (das Objekt bleibt dasselbe).
                    style={DRAUSSEN}
                >
                    <RunningCtaStrip
                        strip={strip}
                        sichtbar={daListe.includes(String(i))}
                        gescrubbt
                        editorMode={editorMode}
                        onCtaAntwort={onCtaAntwort}
                        onFesthalten={(an) => {
                            if (an) festgehalten.current.add(i);
                            else festgehalten.current.delete(i);
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

export default RunningCtaStapel;
